import {
    downloadBuildFile,
    downloadRunLog,
    getComponentBuildAttemptPaths,
    downloadArtifactFile,
    getComponentJobBuildPaths,
    getComponentRunPaths,
    inspectArtifact,
    type ComponentJobsClientOptions,
    type ComponentBuildStatusRecord,
    type ComponentRunStatusRecord,
} from '@secureblob/sdk/componentJobs'

const decoder = new TextDecoder()

export interface ComponentJobWorkspaceWriter {
    writeText: (path: string, content: string) => void
    writeBytes: (path: string, content: Uint8Array) => void
}

export interface MaterializedRunEvent {
    stage: string
    message: string
    updatedAt: string
    totalElapsedSeconds: number
    stageElapsedSeconds: number
    error?: string
}

export interface MaterializeBuildArtifactOptions extends ComponentJobsClientOptions {
    sourcePath: string
    artifactId: string
    workspace: ComponentJobWorkspaceWriter
}

export interface MaterializeFailedBuildStatusOptions {
    sourcePath: string
    status: ComponentBuildStatusRecord
    workspace: ComponentJobWorkspaceWriter
    client?: ComponentJobsClientOptions | null
}

export interface MaterializeRunStatusOptions {
    sourcePath: string
    status: ComponentRunStatusRecord
    startedAt: string
    eventHistory: readonly MaterializedRunEvent[]
    workspace: ComponentJobWorkspaceWriter
    client?: ComponentJobsClientOptions | null
}

export async function materializeBuildArtifactToWorkspace(options: MaterializeBuildArtifactOptions): Promise<void> {
    const { sourcePath, artifactId, workspace, ...client } = options
    const buildPaths = getComponentJobBuildPaths(sourcePath, artifactId)
    const manifest = await inspectArtifact({ ...client, artifactId })

    workspace.writeText(buildPaths.manifest, `${JSON.stringify(manifest, null, 2)}\n`)

    await Promise.all(manifest.files
        .filter(file => file.path !== 'manifest.json')
        .map(async file => {
            const content = await downloadArtifactFile({
                ...client,
                artifactId,
                fileName: file.path,
            })

            writeArtifactFile(workspace, buildPaths.artifactDir, file.path, content)
        }))
}

export async function materializeFailedBuildStatusToWorkspace(options: MaterializeFailedBuildStatusOptions): Promise<void> {
    const { sourcePath, status, workspace, client } = options
    const buildAttemptPaths = getComponentBuildAttemptPaths(sourcePath, status.buildId)

    workspace.writeText(buildAttemptPaths.metadata, `${JSON.stringify({
        buildId: status.buildId,
        sourceSnapshot: status.sourceSnapshot,
        entrypoints: status.entrypoints,
        workspaceBlobId: status.workspaceBlobId,
        artifactId: status.artifactId ?? null,
        status: status.stage,
        message: status.message,
        error: status.error ?? null,
        startedAt: status.startedAt,
        updatedAt: status.updatedAt,
        stageStartedAt: status.stageStartedAt,
        totalElapsedSeconds: status.totalElapsedSeconds,
        stageElapsedSeconds: status.stageElapsedSeconds,
    }, null, 2)}\n`)

    if (!client) {
        return
    }

    await materializeRemoteBuildFile(client, status.buildId, 'build-log.txt', buildAttemptPaths.buildLog, workspace)
}

export async function materializeRunStatusToWorkspace(options: MaterializeRunStatusOptions): Promise<void> {
    const { sourcePath, status, startedAt, eventHistory, workspace, client } = options
    const runPaths = getComponentRunPaths(sourcePath, status.runId)

    workspace.writeText(runPaths.metadata, `${JSON.stringify({
        runId: status.runId,
        artifactId: status.artifactId,
        workspaceBlobId: status.workspaceBlobId,
        sourcePath: status.sourcePath ?? sourcePath,
        startedAt,
        updatedAt: status.updatedAt,
        status: status.stage,
        target: 'server',
        engine: status.engine,
        summary: status.message,
        message: status.message,
        error: status.error ?? null,
        totalElapsedSeconds: status.totalElapsedSeconds,
        stageElapsedSeconds: status.stageElapsedSeconds,
    }, null, 2)}\n`)

    if (eventHistory.length > 0) {
        workspace.writeText(runPaths.stdout, `${eventHistory
            .map(event => formatRunLogLine(event.updatedAt, event.stage, event.message))
            .join('\n')}\n`)

        workspace.writeText(runPaths.events, `${eventHistory
            .map(event => JSON.stringify({
                timestamp: event.updatedAt,
                stage: event.stage,
                message: event.message,
                totalElapsedSeconds: event.totalElapsedSeconds,
                stageElapsedSeconds: event.stageElapsedSeconds,
                error: event.error,
                source: 'component-jobs-control-plane',
            }))
            .join('\n')}\n`)
    }

    const errorEvents = eventHistory.filter(event => typeof event.error === 'string' && event.error.trim().length > 0)
    if (errorEvents.length > 0 || (status.error && status.error.trim().length > 0)) {
        const stderrLines = errorEvents.map(event => formatRunLogLine(
            event.updatedAt,
            event.stage,
            event.error ?? event.message,
        ))

        if (stderrLines.length === 0 && status.error) {
            stderrLines.push(formatRunLogLine(status.updatedAt, status.stage, status.error))
        }

        workspace.writeText(runPaths.stderr, `${stderrLines.join('\n')}\n`)
    }

    if (!client) {
        return
    }

    await Promise.all([
        materializeRemoteRunLog(client, status.runId, 'stdout.log', runPaths.stdout, workspace),
        materializeRemoteRunLog(client, status.runId, 'stderr.log', runPaths.stderr, workspace),
        materializeRemoteRunLog(client, status.runId, 'events.jsonl', runPaths.events, workspace),
        materializeRemoteRunLog(client, status.runId, 'http.jsonl', runPaths.http, workspace),
        materializeRemoteRunLog(client, status.runId, 'mcp.jsonl', runPaths.mcp, workspace),
    ])
}

function writeArtifactFile(
    workspace: ComponentJobWorkspaceWriter,
    artifactDir: string,
    relativePath: string,
    content: Uint8Array,
) {
    const targetPath = `${artifactDir}/${relativePath}`
    if (isTextArtifactPath(relativePath)) {
        workspace.writeText(targetPath, decoder.decode(content))
        return
    }

    workspace.writeBytes(targetPath, content)
}

function isTextArtifactPath(relativePath: string): boolean {
    const normalized = relativePath.trim().toLowerCase()
    return normalized.endsWith('.js')
        || normalized.endsWith('.json')
        || normalized.endsWith('.jsonl')
        || normalized.endsWith('.log')
        || normalized.endsWith('.md')
        || normalized.endsWith('.txt')
}

function formatRunLogLine(updatedAt: string, stage: string, message: string): string {
    return `[${updatedAt}] ${stage}: ${message}`
}

async function materializeRemoteRunLog(
    client: ComponentJobsClientOptions,
    runId: string,
    logName: 'stdout.log' | 'stderr.log' | 'events.jsonl' | 'http.jsonl' | 'mcp.jsonl',
    targetPath: string,
    workspace: ComponentJobWorkspaceWriter,
): Promise<void> {
    try {
        const content = await downloadRunLog({
            ...client,
            runId,
            logName,
        })

        if (content.trim().length === 0) {
            return
        }

        workspace.writeText(targetPath, ensureTrailingNewline(content))
    } catch (error) {
        if (isNotFoundError(error)) {
            return
        }
        throw error
    }
}

async function materializeRemoteBuildFile(
    client: ComponentJobsClientOptions,
    buildId: string,
    fileName: 'build-log.txt',
    targetPath: string,
    workspace: ComponentJobWorkspaceWriter,
): Promise<void> {
    try {
        const content = await downloadBuildFile({
            ...client,
            buildId,
            fileName,
        })

        const text = decoder.decode(content)
        if (text.trim().length === 0) {
            return
        }

        workspace.writeText(targetPath, ensureTrailingNewline(text))
    } catch (error) {
        if (isNotFoundError(error)) {
            return
        }
        throw error
    }
}

function ensureTrailingNewline(content: string): string {
    return content.endsWith('\n') ? content : `${content}\n`
}

function isNotFoundError(error: unknown): boolean {
    return typeof error === 'object'
        && error !== null
        && 'code' in error
        && (error as { code?: string }).code === 'NOT_FOUND'
}
